/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import dayjs, { Dayjs } from "dayjs"
import { FEConfig } from "store/duck/global.type"
import { BusinessType, MerchantStoreInfo } from "types/models/menu"
import { ListReviewsRequest } from "types/api/menuFeedback.type"
import { ServiceType } from "constants/feedback"
import { isNil } from "lodash"

export const mapCityNameToMerchantIDs = ({
  cityName,
  outlets,
}: {
  cityName: string
  outlets: MerchantStoreInfo[]
}) => {
  return outlets
    .filter(o => o.city.toLowerCase() === cityName.toLowerCase())
    .map(out => out.merchantID)
}

export const mapCityNameToOutlets = ({
  cityName,
  outlets,
}: {
  cityName?: string
  outlets?: MerchantStoreInfo[]
}): MerchantStoreInfo[] => {
  if (!cityName && outlets) return outlets
  if (!outlets || (!cityName && !outlets)) return []

  return outlets.filter(o => o.city.toLowerCase() === cityName?.toLowerCase())
}

export const mapBusinessTypeToOutlets = ({
  businessType,
  outlets,
}: {
  businessType: BusinessType
  outlets: MerchantStoreInfo[]
}): MerchantStoreInfo[] => {
  return outlets.filter(o => {
    // special case when undefined means Food
    if (businessType === BusinessType.Food && !o.businessType) return true
    return o.businessType === businessType
  })
}

export const mapServiceTypeToOutlets = ({
  serviceType,
  outlets,
}: {
  serviceType: ServiceType
  outlets: MerchantStoreInfo[]
}): MerchantStoreInfo[] => {
  if (serviceType === ServiceType.ALL) {
    return outlets
  }

  return outlets.filter(o => o.deliverOption?.includes(serviceType as string))
}

export const filterOutlets = ({
  cityName,
  serviceType,
  businessType,
  outlets,
}: {
  cityName?: string
  serviceType?: ServiceType
  businessType?: BusinessType
  outlets: MerchantStoreInfo[]
}) => {
  let filteredOutlets = outlets
  if (!isNil(cityName))
    filteredOutlets = mapCityNameToOutlets({
      cityName,
      outlets: filteredOutlets,
    })
  if (!isNil(businessType)) {
    filteredOutlets = mapBusinessTypeToOutlets({
      businessType,
      outlets: filteredOutlets,
    })
  }
  if (!isNil(serviceType))
    filteredOutlets = mapServiceTypeToOutlets({
      serviceType,
      outlets: filteredOutlets,
    })

  return filteredOutlets
}

export const filterDisabledDate = ({
  current,
  feConfig,
  filterState,
}: {
  current: Dayjs
  feConfig?: FEConfig
  filterState?: ListReviewsRequest
}) => {
  const dineInLimit: number | undefined =
    feConfig?.data?.dineInReviewConfig?.ListDineInReviewMaxMonthCount
  const deliveryLimit: number | undefined =
    feConfig?.data?.reviewReportConfig?.listReviewMaxDayCount

  if (filterState?.serviceType === ServiceType.DINEIN && dineInLimit)
    return (
      current &&
      current >
        dayjs()
          .add(dineInLimit, "months")
          .endOf("day")
    )

  if (deliveryLimit)
    return (
      current &&
      current >
        dayjs()
          .add(deliveryLimit, "days")
          .endOf("day")
    )

  return current && current > dayjs().endOf("day")
}
